<script setup lang="ts">
import BalmMediaPlayer from '@/vue/molecules/balm-media-player.vue';
import SvgIllustration from '@/vue/atoms/svg-illustration.vue';
import {computed, type CSSProperties, onMounted, ref} from 'vue';
import {calculateMediaUrl} from '@/ts/utils/pure-functions';
import {useLightbox} from '@/ts/composables/pure/use-lightbox';
import {useLogger} from '@/ts/composables/pure/use-logger';

const props = withDefaults(defineProps<{
  videoSrc: string;
  posterSrc?: string;
  posterAlt?: string;
  isLightBoxed?: boolean;
  title?: string;
  overlayText?: string;
  overlayTextPosition?: 'top-left' | 'bottom-right';
  overlayTitleWithBackground?: boolean;
  containerClass?: string;
  containerStyle?: CSSProperties;
  /** Start muted playback as soon as the video can play. Passed straight to the player. */
  autoplayMuted?: boolean;
}>(), {
  isLightBoxed: false,
});

const emit = defineEmits<{
  /**
   * Playback began, which callers use to take their own furniture off the frame: the tile on the
   * Today page drops the gradient and the copy it draws over this, the way the design does.
   *
   * Every time it begins. A pause does not put anything back — that is the caller's business, and
   * the Today tile only puts its copy back on `autoplayEnded` — but the reader's own play after
   * that has to be heard, or the copy stays printed over a running video.
   */
  playbackStarted: [];
  /** Half of the video has been played (played, not reached by dragging). Fired once. */
  halfWatched: [];
  /**
   * A video that played itself has finished and is back at rest: rewound, sound returned. Never for
   * a playback the reader started or took over — that one is left where they left it.
   */
  autoplayEnded: [];
}>();

const videoHasStarted = ref(false);

function onPlaybackStarted(): void {
  videoHasStarted.value = true;
  emit('playbackStarted');
}

const {open} = useLightbox();
const {error: logError} = useLogger();

const videoUrl = computed(() => calculateMediaUrl(props.videoSrc));
const posterUrl = computed(() => props.posterSrc ? calculateMediaUrl(props.posterSrc) : '');

const FALLBACK_VIDEO_WIDTH = 1920;
const FALLBACK_VIDEO_HEIGHT = 1080;

// Store actual video dimensions once loaded
const actualDimensions = ref<{width: number; height: number} | null>(null);
// Store the promise so we can await it if user clicks before it completes
let dimensionsPromise: Promise<{width: number; height: number}> | null = null;

function getVideoMimeType(url: string): string {
  const extension = url?.split('.').pop()?.toLowerCase() ?? '';
  const mimeTypes: Record<string, string> = {
    mp4: 'video/mp4',
    webm: 'video/webm',
    ogg: 'video/ogg',
    ogv: 'video/ogg',
    mov: 'video/quicktime',
  };

  return mimeTypes[extension] ?? 'video/mp4';
}

async function getVideoDimensions(url: string): Promise<{width: number; height: number}> {
  return new Promise((resolve) => {
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.src = url;

    video.onloadedmetadata = (): void => {
      const width = video.videoWidth || FALLBACK_VIDEO_WIDTH;
      const height = video.videoHeight || FALLBACK_VIDEO_HEIGHT;
      resolve({width, height});
    };

    video.onerror = (): void => {
      resolve({
        width: FALLBACK_VIDEO_WIDTH,
        height: FALLBACK_VIDEO_HEIGHT,
      });
    };
  });
}

// The lightbox itself is created on the first click; only the video's dimensions are fetched ahead.
onMounted(() => {
  if (props.isLightBoxed) {
    // Pre-fetch actual video dimensions in the background
    dimensionsPromise = getVideoDimensions(videoUrl.value);
    void dimensionsPromise.then((dimensions) => {
      actualDimensions.value = dimensions;
    });
  }
});

let isOpening = false;

async function handleVideoClick(): Promise<void> {
  if (!props.isLightBoxed || isOpening) {
    return;
  }

  isOpening = true;

  try {
    // If dimensions aren't ready yet, wait for them
    let dimensions = actualDimensions.value;
    if (!dimensions && dimensionsPromise) {
      dimensions = await dimensionsPromise;
    }

    // Use actual dimensions if available, otherwise use fallbacks
    const finalDimensions = dimensions ?? {
      width: FALLBACK_VIDEO_WIDTH,
      height: FALLBACK_VIDEO_HEIGHT,
    };

    await open({
      items: [{
        sources: [{
          src: videoUrl.value,
          type: getVideoMimeType(videoUrl.value),
        }],
        caption: props.title ?? '',
        width: finalDimensions.width,
        height: finalDimensions.height,
      }],
      position: 0,
    });
  } catch (err) {
    // The library is fetched on this first click: a failed fetch would otherwise be a silent dead click.
    logError('Failed to open the lightbox', err)();
  } finally {
    // eslint-disable-next-line require-atomic-updates
    isOpening = false;
  }
}
</script>

<template>
  <div
    :class="['lightbox-video-container', containerClass, {'lightbox-enabled': isLightBoxed}]"
    :style="containerStyle"
    :role="isLightBoxed ? 'button' : undefined"
    :tabindex="isLightBoxed ? 0 : undefined"
    @click="handleVideoClick"
    @keydown.enter="handleVideoClick"
    @keydown.space.prevent="handleVideoClick"
  >
    <balm-media-player
      :src="videoUrl"
      :poster-src="posterUrl"
      :poster-alt="posterAlt"
      :autoplay-muted="autoplayMuted"
      :class="['lightbox-video', {'pointer-events-none': isLightBoxed}]"
      @playback-started="onPlaybackStarted"
      @half-watched="emit('halfWatched')"
      @autoplay-ended="emit('autoplayEnded')"
    />

    <!-- Overlay text (positioned on top of the video, hidden once playback starts) -->
    <div v-if="overlayText && overlayTextPosition === 'bottom-right' && !videoHasStarted" class="text-overlay position-bottom-right">
      <span :class="overlayTitleWithBackground ? 'overlay-title-pill' : 'overlay-title'">
        {{ overlayText }}
      </span>
    </div>

    <div v-if="isLightBoxed" class="lightbox-indicator" aria-hidden="true">
      <svg-illustration
        name="media-maximize.svg"
        inner-class="lightbox-icon"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.lightbox-video-container {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  border-radius: var(--card-border-radius);
  overflow: hidden;

  &.lightbox-enabled {
    cursor: pointer;

    &:hover .lightbox-indicator {
      opacity: 1;
    }

    &:focus-visible {
      outline: 2px solid var(--Primary-primary-color-2);
      outline-offset: 2px;
      border-radius: var(--border-radius);
    }
  }
}

.lightbox-video {
  width: 100%;
  height: 100%;
  display: block;

  &.pointer-events-none {
    pointer-events: none;
  }
}

.text-overlay {
  position: absolute;
  pointer-events: none;

  &.position-bottom-right {
    display: none;
    bottom: var(--Spacing-spacing-6);
    right: var(--Spacing-spacing-7);

    @include screen-md {
      display: block;
      bottom: var(--Spacing-spacing-7);
      right: var(--Spacing-spacing-6);
    }

    @include screen-lg {
      display: block;
      bottom: var(--Spacing-spacing-7);
      right: var(--Spacing-spacing-7);
    }
  }
}

.overlay-title {
  @include font-body-bold;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
  margin: 0;
}

.overlay-title-pill {
  @include font-body-bold;
  display: inline-block;
  color: var(--Light-Scheme-Neutral-UI-neutral-ui-100);
  background-color: rgba(255, 255, 255, 0.9);
  padding: 0 var(--Spacing-spacing-2);
  border-radius: 100px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.lightbox-indicator {
  position: absolute;
  top: var(--Spacing-spacing-2);
  right: var(--Spacing-spacing-2);
  width: 32px;
  height: 32px;
  background-color: var(--Neutral-UI-neutral-ui-100);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--Neutral-UI-neutral-ui-0);
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
  z-index: 10;

  :deep(.lightbox-icon) {
    width: 18px;
    height: 18px;
  }

  @include screen-md {
    width: 40px;
    height: 40px;

    :deep(.lightbox-icon) {
      width: 20px;
      height: 20px;
    }
  }
}
</style>
